import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from "@hello-pangea/dnd";
import { type RefObject, useEffect, useRef, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import type { MultiValue } from "react-select";
import {
  type DocumentSubType,
  DocumentType,
  type MiscContent,
  MiscContentType,
  type Party,
  type PartyPosition,
  Representative,
  Respondent,
  Role,
} from "@/briefpoint-client";
import Button from "@/components/Button";
import { replaceTokens } from "@/components/DocumentWizard/SpecialInterrogatoriesStep/Selections/utils";
import TagsSelect, { type TagOption } from "@/components/Select/TagsSelect";
import useMiscContent from "@/hooks/useMiscContent";
import type {
  CaseResponse,
  ClientResponse,
  DefinitionItem,
  DefinitionResponse,
  JurisdictionResponse,
  QuestionGroup,
  QuestionItem,
  UserResponse,
} from "@/lib/types";
import {
  buildQuestionKey,
  buildQuestionTitle,
  convertPartyPositionToPropoundingParty,
} from "@/screens/DocumentGeneration/GenerateDiscoveryRequestPage";
import { HighlightDefinitions } from "@/utils";
import Question from "../Question";

export const punctuationRegexForDefAliases = new RegExp(/[,;:|]/g);

export function definitionContentPropound(
  def: DefinitionResponse,
  _case: CaseResponse,
  client: string,
  docType: DocumentSubType,
  party: Party,
) {
  if (!def.content) {
    return "undefined";
  }

  return (
    <>
      "<b>{def.term}</b>"{definitionAliasDisplay(def.aliases ?? undefined)}{" "}
      {replaceTokens(
        {
          propoundingParty: convertPartyPositionToPropoundingParty(
            _case?.clientPosition,
          ),
          documentType: DocumentType.DiscoveryRequest,
          documentSubType: docType,
          respondent: Respondent.Custom,
          respondentCustom: party.name,
          respondingPartyName: party.name,
          propoundingPartyName: client,
          respondingParty: convertPartyPositionToPropoundingParty(
            party?.position,
          ),
        },
        def.content || "",
        true,
        _case?.representativeType,
      )}
    </>
  );
}

export function definitionAliasDisplay(aliases?: string) {
  if (!aliases) {
    return null;
  }

  const split = aliases
    .split(punctuationRegexForDefAliases)
    .map<React.ReactNode>((x) => (
      <>
        "<b key={x.trim()}>{x.trim()}</b>"
      </>
    ));
  return (
    <>
      {split.length === 1 ? " and " : ", "}
      {split.length === 1
        ? split[0]
        : split.reduce((prev, curr, index) => [
            prev,
            index === split.length - 1 ? " and " : ", ",
            curr,
          ])}
    </>
  );
}

export function buildCaseTypeOptions(
  caseType: string,
  jurisdiction: JurisdictionResponse | undefined,
  selected: string | undefined,
  handleChange: (value?: string) => void,
) {
  let typeClass = jurisdiction?.caseClasses?.find((x) => x.id === caseType);

  if (typeClass) {
    return (
      <Form.Check
        id="new-casetype-class"
        type="radio"
        label={typeClass.name}
        inline
        value={typeClass.id}
        checked={selected === typeClass.id}
        onChange={(_) => handleChange(typeClass?.id)}
      />
    );
  }

  typeClass = jurisdiction?.caseClasses?.find((x) =>
    x.subTypes?.find((s) => s.id === caseType),
  );

  const type = typeClass?.subTypes?.find((x) => x.id === caseType);

  return (
    <>
      <Form.Check
        id="new-casetype-class"
        type="radio"
        inline
        label={typeClass?.name}
        value={typeClass?.id}
        checked={selected === typeClass?.id}
        onChange={(_) => handleChange(typeClass?.id)}
      />
      <Form.Check
        id="new-casetype-type"
        type="radio"
        inline
        label={type?.shortName}
        value={type?.id}
        checked={selected === type?.id}
        onChange={(_) => handleChange(type?.id)}
      />
    </>
  );
}

interface Props {
  availableTags: Map<number, string>;
  user?: UserResponse;
  _case?: CaseResponse;
  client: ClientResponse;
  startingNumber: number;
  jurisdiction?: JurisdictionResponse;
  questions: QuestionGroup[] | undefined;
  docType?: DocumentSubType;
  definitions?: DefinitionItem[];
  otherParty?: Party;
  handleOpenLibrary: (tagSectionToOpen?: string) => void;
  handleDeleteItem: (id: number | undefined) => void;
  checkForDefinitionUpdate: (
    questions: QuestionItem[],
    existing?: DefinitionItem[],
  ) => Promise<void>;
  setQuestions: React.Dispatch<
    React.SetStateAction<QuestionGroup[] | undefined>
  >;
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
  toastRequestOpen?: boolean;
  setToastRequestOpen: React.Dispatch<React.SetStateAction<boolean>>;
  scrollRef?: RefObject<HTMLDivElement | null>;
  toastCurTagId?: number | null;
}

export default function Questions({
  _case,
  user,
  client,
  startingNumber,
  jurisdiction,
  availableTags,
  questions,
  docType,
  definitions,
  otherParty,
  handleDeleteItem,
  handleOpenLibrary,
  checkForDefinitionUpdate,
  setQuestions,
  setIsDirty,
  toastRequestOpen,
  setToastRequestOpen,
  scrollRef,
  toastCurTagId,
}: Props) {
  const [isAdding, setIsAdding] = useState(false);
  const [newRequestText, setNewRequestText] = useState("");
  const newRef = useRef<HTMLTextAreaElement>(null);
  const [selectedTags, setSelectedTags] = useState<number[]>();
  const [selectedCaseType, setSelectedCaseType] = useState<string>();
  const [saveToFirm, setSaveToFirm] = useState(false);
  const [selectedRespondentType, setSelectedRespondentType] =
    useState<PartyPosition>();
  const [selectedRepresentative, setSelectedRepresentative] =
    useState<boolean>();
  const [editQuestionText, setEditQuestionText] = useState("");
  const [editQuestionGroup, setEditQuestionGroup] = useState<number>();
  const [editQuestionNumber, setEditQuestionNumber] = useState<number>();
  const [saveQuestionToLibrary, setSaveQuestionToLibrary] =
    useState<boolean>(false);
  const [editActive, setEditActive] = useState<boolean>(false);
  const [defaultsAdded, setDefaultsAdded] = useState<string[]>([]);
  const [, , , , , , createMiscContent] = useMiscContent(false);
  const [startIdx, setStartIdx] = useState<number | null>(null);
  const [isDragChecked, setIsDragChecked] = useState<boolean>(false);

  const handleInputChange = (content: string) => {
    setNewRequestText(content);
  };

  useEffect(() => {
    if (!questions && defaultsAdded.length) {
      setDefaultsAdded([]);
    }
  }, [questions, defaultsAdded]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: watch newRef
  useEffect(() => {
    if (isAdding && newRef.current) {
      newRef.current.focus();
    }
  }, [isAdding, newRef]);

  // This ensures that when the user starts the click/drag operation of selecting multiple select boxes AND if the
  // user holds off their mouse outside the question div boundaries, the drag-to-select functionality will still work correctly.
  useEffect(() => {
    if (startIdx !== null) {
      const handleGlobalMouseUp = () => {
        if (startIdx !== null) {
          setIsDirty(true);
        }
        setStartIdx(null);
      };

      document.addEventListener("mouseup", handleGlobalMouseUp);

      return () => {
        document.removeEventListener("mouseup", handleGlobalMouseUp);
      };
    }
  }, [startIdx, setIsDirty]);

  // Update the questions toggle if the 'Add now' button is clicked
  useEffect(() => {
    if (toastRequestOpen) {
      setIsAdding(true);
      setSaveQuestionToLibrary(true);

      if (toastCurTagId) {
        if (!selectedTags?.length) {
          setSelectedTags([toastCurTagId]);
        }
      }
    }
  }, [toastRequestOpen, toastCurTagId, selectedTags]);

  // Helper function to sort questions
  function handleQuestionSort(
    questions: QuestionItem[] | undefined,
  ): QuestionItem[] {
    return [...(questions || [])].sort(
      (a, b) =>
        (a.number ?? a.order ?? Number.MAX_SAFE_INTEGER) -
        (b.number ?? b.order ?? Number.MAX_SAFE_INTEGER),
    );
  }

  function handleMouseDown(
    groupIndex: number,
    questionIndex: number,
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) {
    // Don't run this function if the user is clicking the drag icon
    const isDragHandleClick =
      e.target instanceof Element &&
      (e.target.closest('[data-drag-handle="true"]') !== null ||
        e.target.tagName === "svg" ||
        e.target.closest("svg") !== null);

    if (isDragHandleClick) return;

    e.preventDefault();

    // Get sorted questions once
    const group = questions?.[groupIndex];
    if (!group?.questions) return;

    const sortedQuestions = handleQuestionSort(group.questions);
    const clickedQuestion = sortedQuestions[questionIndex];
    if (!clickedQuestion) return;

    // Set new state based on current state
    const newCheckedState = !clickedQuestion.isChecked;
    setIsDragChecked(newCheckedState);

    // Update questions with new state
    setQuestions((q) => {
      const updatedQuestions = [...(q ?? [])];
      const updatedGroup = updatedQuestions[groupIndex];

      if (updatedGroup?.questions) {
        const sortedQs = handleQuestionSort(updatedGroup.questions);
        const question = sortedQs[questionIndex];
        if (question) {
          question.isChecked = newCheckedState;
        }
      }

      return updatedQuestions;
    });

    setStartIdx(questionIndex);
  }

  function handleIndividualQuestionCheck(
    groupIndex: number,
    questionIndex: number,
  ) {
    const updatedCheckboxes = [...(questions ?? [])];
    const group = updatedCheckboxes[groupIndex];
    if (group?.questions) {
      const sortedQuestions = handleQuestionSort(group.questions);
      const question = sortedQuestions[questionIndex];
      if (question) {
        question.isChecked = !question.isChecked;
      }
    }
    setQuestions(updatedCheckboxes);
    setIsDirty(true);
  }

  function handleMouseEnter(groupIndex: number, questionIndex: number) {
    if (startIdx !== null) {
      setQuestions((q) => {
        const updatedCheckboxes = [...(q ?? [])];
        const group = updatedCheckboxes[groupIndex];

        if (group?.questions) {
          const sortedQuestions = handleQuestionSort(group.questions);
          const enteredQuestion = sortedQuestions[questionIndex];

          if (enteredQuestion) {
            enteredQuestion.isChecked = isDragChecked;
          }
        }

        return updatedCheckboxes;
      });
    }
  }

  function handleMouseUp() {
    if (startIdx !== null) {
      setIsDirty(true);

      // Extract definitions for all questions that were changed during the drag operation
      if (questions) {
        const allQuestions = questions.flatMap(
          (group) => group.questions || [],
        );
        checkForDefinitionUpdate(allQuestions, definitions);
      }
    }
    setStartIdx(null);
  }

  function handleEditingQuestion(
    original: string,
    groupIndex: number,
    questionIndex: number,
  ) {
    setEditActive(true);
    setEditQuestionText(original);
    setEditQuestionGroup(groupIndex);
    setEditQuestionNumber(questionIndex);
  }

  function handleOnKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      setEditQuestionText("");
      setEditQuestionGroup(undefined);
      setEditQuestionNumber(undefined);
      setEditActive(false);
    } else if (e.key === "Enter") {
      handleEditingBlur();
    }
  }

  function handleEditingBlur() {
    if (editQuestionGroup === undefined || editQuestionNumber === undefined) {
      setEditQuestionText("");
      setEditQuestionGroup(undefined);
      setEditQuestionNumber(undefined);
      setEditActive(false);
      return;
    }

    const previous =
      questions?.[editQuestionGroup]?.questions?.[editQuestionNumber]
        ?.displayText;
    if (editQuestionText && editQuestionText !== previous && docType) {
      const displayText = replaceTokens(
        {
          propoundingParty: convertPartyPositionToPropoundingParty(
            _case?.clientPosition,
          ),
          documentType: DocumentType.DiscoveryRequest,
          documentSubType: docType,
          respondent: Respondent.Custom,
          respondentCustom: otherParty?.name,
          respondingPartyName: otherParty?.name,
          propoundingPartyName: client.name,
          respondingParty: convertPartyPositionToPropoundingParty(
            otherParty?.position,
          ),
        },
        editQuestionText || "",
        true,
        _case?.representativeType,
      );
      setQuestions((q) => {
        const update = [...(q ?? [])];
        const group = update[editQuestionGroup];

        if (group?.questions?.[editQuestionNumber]) {
          const question = group.questions[editQuestionNumber];
          if (question) {
            question.text = editQuestionText;
            question.displayText = displayText;
          }
        }

        return update;
      });
      checkForDefinitionUpdate(
        [{ text: displayText, isChecked: true }],
        definitions,
      );
    }
    setEditQuestionText("");
    setEditQuestionGroup(undefined);
    setEditQuestionNumber(undefined);
    setEditActive(false);
    setIsDirty(true);
  }

  async function handleNewSave() {
    if (newRequestText) {
      const newQuestion: QuestionItem = {
        isChecked: true,
        text: newRequestText,
      };

      // questions added without "Add to library for future use" go in their own group
      // TODO: could probably combine these 2 branches w/ some smarter logic
      if (!saveQuestionToLibrary) {
        const groupId = "-1";
        const update = [...(questions ?? [])];
        let group = update.find((x) => x.key === groupId);

        if (!group) {
          group = { key: "-1", title: "Document Specific", questions: [] };
          update.push(group);
        }

        group.questions?.push(newQuestion);

        setQuestions(update);
      } else {
        // Save to library first, then add to state
        const toSave: MiscContent = {
          caseTypeId: selectedCaseType,
          content: newRequestText,
          type: MiscContentType.DiscoveryQuestion,
          documentType: DocumentType.DiscoveryRequest,
          documentSubType: docType,
          isForRepresentative: selectedRepresentative,
          tagIds: selectedTags,
          sharedWithFirmId: saveToFirm ? user?.firmId : null,
          respondentType:
            selectedRespondentType === undefined
              ? undefined
              : convertPartyPositionToPropoundingParty(selectedRespondentType),
        };
        const groupId = buildQuestionKey(toSave);

        const questionGroup = questions?.find((x) => x.key === groupId);

        if (questionGroup) {
          const maxOrder = questionGroup.questions?.reduce<number | undefined>(
            (prev, current) => {
              return !prev
                ? (current.order ?? undefined)
                : current.order && current.order > prev
                  ? current.order
                  : prev;
            },
            undefined,
          );
          toSave.order = (maxOrder ?? 0) + 1;
        }

        // Save to API first
        const miscContent = await createMiscContent(toSave);

        // Update the question with the returned ID
        newQuestion.questionId = miscContent.id;

        // Now add to state with the complete question data
        const update = [...(questions ?? [])];

        let group = update.find((x) => x.key === groupId);

        if (!group && docType && jurisdiction?.caseClasses) {
          group = {
            key: groupId,
            title: buildQuestionTitle(
              miscContent,
              docType,
              jurisdiction.caseClasses,
              availableTags,
              _case?.jurisdiction ?? "",
            ),
            questions: [],
          };
          update.push(group);
        }

        group?.questions?.push(newQuestion);

        setQuestions(update);
      }

      checkForDefinitionUpdate([newQuestion], definitions);
    }
    setIsDirty(true);
    closeAddNew();
  }

  function closeAddNew(clear = false) {
    if (clear) {
      setSelectedTags([]);
      setSelectedCaseType(undefined);
      setSelectedRepresentative(undefined);
      setSelectedRespondentType(undefined);
    }
    setNewRequestText("");
    setIsAdding(false);
    setToastRequestOpen(false);
  }

  function handleLocalTagChange(value: MultiValue<TagOption>) {
    const mapped = value.map((v) => v.value).filter((v) => v !== null);
    setSelectedTags(mapped);
  }

  function handleLocalCaseTypeChange(value?: string) {
    setSelectedCaseType(value);
  }

  function handleAddToLibrary() {
    setSelectedCaseType(_case?.caseType ?? undefined);
    setSaveQuestionToLibrary(!saveQuestionToLibrary);
  }

  const onDragEnd = async (result: DropResult) => {
    const { source, destination } = result;

    // Don't do anything if the item is moved outside of a droppable area or stayed in place
    if (
      !destination ||
      (source.droppableId === destination.droppableId &&
        source.index === destination.index)
    )
      return;

    // Parse group indices
    const sourceGroupId = source.droppableId;
    const destGroupId = destination.droppableId;
    const sourceGroupIndex = Number.parseInt(
      sourceGroupId.replace("group-", ""),
      10,
    );

    // Cache original questions state for rollback on error
    const cachedOriginalQuestions = JSON.parse(JSON.stringify(questions));

    try {
      if (sourceGroupId === destGroupId) {
        const group = questions?.[sourceGroupIndex];
        if (!group?.questions?.length) return;

        const sortedQuestions = [...group.questions].sort((a, b) => {
          if (a.number === 0) return 1;
          if (b.number === 0) return -1;
          return (
            (a.number ?? Number.MAX_SAFE_INTEGER) -
            (b.number ?? Number.MAX_SAFE_INTEGER)
          );
        });

        const [reorderedItem] = sortedQuestions.splice(source.index, 1);
        sortedQuestions.splice(destination.index, 0, reorderedItem);

        const updatedQuestions = sortedQuestions.map((item, index) => ({
          ...item,
          number: index + 1,
        }));

        setQuestions((currentQuestions) => {
          if (!currentQuestions) return currentQuestions;
          const newQuestions = JSON.parse(JSON.stringify(currentQuestions));
          newQuestions[sourceGroupIndex].questions = updatedQuestions;
          return newQuestions;
        });
        setIsDirty(true);
      }
    } catch (error) {
      console.error("Failed to save question reordering:", error);
      // Revert to original state on error
      setQuestions(cachedOriginalQuestions);
    }
  };
  // Precalculate / Watch the numbers of each value, @hello-pangea/dnd seems to do some weirdness when incrementing them up the old way...
  const questionData: { isChecked: boolean; globalIndex: number }[] = [];
  let globalQuestionIndex = 0;

  if (questions) {
    for (const group of questions) {
      const sortedQuestions = handleQuestionSort(group.questions);

      for (const q of sortedQuestions) {
        questionData.push({
          isChecked: q.isChecked || false,
          globalIndex: globalQuestionIndex++,
        });
      }
    }
  }

  const questionNumbers: number[] = [];
  let sequentialNumber = 1;

  for (const q of questionData) {
    if (q.isChecked) {
      questionNumbers[q.globalIndex] = sequentialNumber++;
    } else {
      questionNumbers[q.globalIndex] = 0;
    }
  }

  let questionCount = 0;
  return (
    <div key="questions" className="questions">
      {!!questions?.length &&
        questions.map((group, groupIndex) => {
          const sortedQuestions = handleQuestionSort(group.questions);

          if (group.questions?.length === 0) {
            return null;
          }

          return (
            <div key={`questiongroup-${groupIndex}`}>
              <div
                key={`question-title-${groupIndex}`}
                className="group-title-container"
              >
                <h3>
                  {group.title}
                  {!!group.defaultQuestions?.length &&
                    (defaultsAdded.find((x) => x === group.key) ? (
                      <span className="bp-defaults-added">{`Added Briefpoint Defaults (${group.defaultQuestions.length})`}</span>
                    ) : (
                      group.questions?.[groupIndex]?.questionId && (
                        <Button
                          variant="link"
                          onClick={() =>
                            handleOpenLibrary(
                              group.questions?.[
                                groupIndex
                              ]?.questionId?.toString(),
                            )
                          }
                        >{`Add Briefpoint Defaults (${group.defaultQuestions.length})`}</Button>
                      )
                    ))}
                </h3>
              </div>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId={`group-${groupIndex}`}>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className="questions-droppable-container"
                    >
                      {sortedQuestions.map((q, questionIndex) => {
                        const groupStartIndex = questions
                          .slice(0, groupIndex)
                          .reduce(
                            (acc, g) => acc + (g.questions?.length || 0),
                            0,
                          );
                        const globalIndex = groupStartIndex + questionIndex;
                        const questionNumber =
                          questionNumbers[globalIndex] || 0;
                        const stableId = q.interrogatoryId
                          ? `question-${q.interrogatoryId}`
                          : `group-${groupIndex}-question-${questionIndex}-${q.text?.substring(0, 10)}`;
                        const currentQuestionCount = questionCount++;
                        return (
                          <Draggable
                            key={stableId}
                            draggableId={stableId}
                            index={questionIndex}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                style={{
                                  ...provided.draggableProps.style,
                                  background: snapshot.isDragging
                                    ? "#f0f0f0"
                                    : "transparent",
                                  marginBottom: "8px",
                                }}
                              >
                                <Question
                                  key={`question-${questionCount++}`}
                                  q={q}
                                  startingNumber={startingNumber}
                                  questionIndex={questionIndex}
                                  groupIndex={groupIndex}
                                  questionCount={currentQuestionCount + 1}
                                  checkedQuestionCount={questionNumber}
                                  editQuestionGroup={editQuestionGroup}
                                  editQuestionNumber={editQuestionNumber}
                                  editQuestionText={editQuestionText}
                                  setEditQuestionText={setEditQuestionText}
                                  editActive={editActive}
                                  highlightDefinitions={(
                                    text: string,
                                    key: string,
                                  ) =>
                                    HighlightDefinitions({
                                      text,
                                      key,
                                      definitions,
                                      _case,
                                      clientName: client.name || "",
                                      docType,
                                      otherParty,
                                    })
                                  }
                                  handleEditingBlur={handleEditingBlur}
                                  handleEditingQuestion={handleEditingQuestion}
                                  handleDeleteItem={handleDeleteItem}
                                  handleOnKeyDown={handleOnKeyDown}
                                  handleMouseDown={handleMouseDown}
                                  handleMouseEnter={handleMouseEnter}
                                  handleMouseUp={handleMouseUp}
                                  handleIndividualQuestionCheck={
                                    handleIndividualQuestionCheck
                                  }
                                  dragHandleProps={provided.dragHandleProps}
                                />
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          );
        })}
      {!!isAdding && (
        <div className="input-wrapper" ref={scrollRef}>
          <label htmlFor="new-request-text" className="accordion-item-header">
            Add New Request
          </label>
          <textarea
            id={"new-request-text"}
            className="form-control fs-7 request-fac-text"
            title="new-request-text"
            ref={newRef}
            onChange={(e) => handleInputChange(e.target.value)}
          />
          <div className="lib-checkbox-container">
            <Form.Check
              id={"library-add-check"}
              type={"checkbox"}
              label="Add to library for future use"
              checked={saveQuestionToLibrary}
              onChange={handleAddToLibrary}
            />
          </div>
          {saveQuestionToLibrary && (
            <>
              {!!user?.role?.includes(Role.FirmAdmin) && (
                <Form.Group id="new-firm-select" className="indent-radio">
                  <Form.Check
                    id="new-firm-select-personal"
                    type="radio"
                    inline
                    label="Personal Library"
                    checked={!saveToFirm}
                    onChange={(_) => setSaveToFirm(false)}
                  />
                  <Form.Check
                    id="new-firm-select-firm"
                    type="radio"
                    inline
                    label="Firm Library"
                    checked={saveToFirm}
                    onChange={(_) => setSaveToFirm(true)}
                  />
                </Form.Group>
              )}
              <Row>
                {!!_case?.caseType && (
                  <Col md={5}>
                    <label htmlFor="new-casetype">Case Type</label>
                    <Form.Group id="new-casetype" className="indent-radio">
                      <Form.Check
                        id="new-casetype-any"
                        type="radio"
                        inline
                        label="Any"
                        value={undefined}
                        checked={selectedCaseType === undefined}
                        onChange={(_) => handleLocalCaseTypeChange(undefined)}
                      />
                      {buildCaseTypeOptions(
                        _case.caseType,
                        jurisdiction,
                        selectedCaseType,
                        handleLocalCaseTypeChange,
                      )}
                    </Form.Group>
                  </Col>
                )}
                <Col md={4}>
                  <label htmlFor="new-position-select">
                    Respondent Position
                  </label>
                  <Form.Group id="new-position-select" className="indent-radio">
                    <Form.Check
                      id="new-position-select-any"
                      type="radio"
                      inline
                      label="Any"
                      checked={!selectedRespondentType}
                      onChange={(_) => setSelectedRespondentType(undefined)}
                    />
                    <Form.Check
                      id="new-position-select-party"
                      type="radio"
                      inline
                      label={otherParty?.position}
                      checked={!!selectedRespondentType}
                      onChange={(_) =>
                        setSelectedRespondentType(otherParty?.position)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <label htmlFor="new-representative-select">
                    Representative Action
                  </label>
                  <Form.Group
                    id="new-representative-select"
                    className="indent-radio"
                  >
                    <Form.Check
                      id="new-representative-select-any"
                      type="radio"
                      inline
                      label="Any"
                      checked={selectedRepresentative === undefined}
                      onChange={(_) => setSelectedRepresentative(undefined)}
                    />
                    <Form.Check
                      id="new-representative-select-party"
                      type="radio"
                      inline
                      label={
                        _case?.representativeType ===
                          Representative.ClassAction ||
                        _case?.representativeType === Representative.Paga
                          ? "Representative (PAGA, Class Action, etc.)"
                          : "Individual"
                      }
                      checked={selectedRepresentative !== undefined}
                      onChange={(_) =>
                        setSelectedRepresentative(
                          _case?.representativeType ===
                            Representative.ClassAction ||
                            _case?.representativeType === Representative.Paga,
                        )
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
              <TagsSelect
                id="case-tags"
                label="Tags"
                availableTags={availableTags}
                selected={selectedTags}
                onChange={handleLocalTagChange}
              />
            </>
          )}
        </div>
      )}
      <div key={"add-request-wrapper"} className="add-request-button-wrapper">
        {isAdding ? (
          <>
            <Button
              variant={"default"}
              style={{ padding: "0px 20px" }}
              className="btn-sm"
              onClick={handleNewSave}
            >
              Save
            </Button>
            <div className="close-link-container">
              <Button
                variant={"link"}
                className="btn-sm"
                onClick={() => closeAddNew(true)}
              >
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <Button
            variant={"outline"}
            className="btn-sm"
            onClick={() => setIsAdding(true)}
          >
            + Add Another Request
          </Button>
        )}
      </div>
    </div>
  );
}
